import React, { useState } from "react";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { toPersianNumber } from "@/hooks/useThousandsSeparator";
import {
  Pagination,
  PaginationItem,
  useTheme,
  useMediaQuery,
  TextField,
  CircularProgress,
} from "@mui/material";
import BoxKit from "@/shared/components/kit/box";
import CustomSelect from "@/shared/components/common/select";
import CustomButton from "@/shared/components/kit/button";

type TableProps = {
  dataHeader: { id: number | string; title: string }[];
  dataTbody: any[];
  CustomSelectOption: number[];
  pagination?: {
    current_page: number;
    per_page: number;
    total: number;
  };
  onPageChange?: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  onDelete?: (id: any) => void;
  deleteLoading?: boolean;
  onActionClick?: (row: any) => void;
  actionLoading?: boolean;
  search?: boolean;
};

const TableWallet = (props: Partial<TableProps>) => {
  const {
    dataHeader,
    dataTbody = [],
    pagination,
    onPageChange,
    onItemsPerPageChange,
    onDelete,
    deleteLoading,
    CustomSelectOption = [5, 10, 15, 20],
    onActionClick,
    actionLoading = false,
    search = true,
  } = props;

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const [localPage, setLocalPage] = useState(1);
  const [localItemsPerPage, setLocalItemsPerPage] = useState<number | string>(
    10
  );
  const hasServerPagination = !!pagination;
  const currentPage = hasServerPagination
    ? pagination!.current_page
    : localPage;
  const itemsPerPage = hasServerPagination
    ? pagination!.per_page
    : Number(localItemsPerPage) || 10;
  const totalItems = hasServerPagination ? pagination!.total : dataTbody.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedData = hasServerPagination
    ? dataTbody
    : dataTbody.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      );

  const handlePageChange = (newPage: number) => {
    if (hasServerPagination) onPageChange?.(newPage);
    else setLocalPage(newPage);
  };

  const handleItemsPerPageChange = (newSize: number) => {
    if (hasServerPagination) onItemsPerPageChange?.(newSize);
    else {
      setLocalItemsPerPage(newSize);
      setLocalPage(1);
    }
  };

  return (
    <StackKit direction="column" className="w-full" sx={{ padding: 1 }}>
            {/* Per-page selector */}
      {hasServerPagination && onItemsPerPageChange && (
        <StackKit
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          sx={{ mt: 2, gap: 1 }}
        >
          <TypoGraphyKit variant="body2">تعداد در صفحه:</TypoGraphyKit>
          <CustomSelect
            label
            value={String(pagination!.per_page)}
            onChange={(value: string | number) =>
              handleItemsPerPageChange(Number(value))
            }
            options={CustomSelectOption ?? [5, 10, 15, 20]}
          />
        </StackKit>
      )}

      {/* Table */}
      <BoxKit
        sx={{
          width: "100%",
          overflowX: "auto",
          position: "relative",
          minHeight: "200px",
        }}
      >
        {actionLoading && (
          <StackKit
            direction="row"
            alignItems="center"
            justifyContent="center"
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 10,
              borderRadius: "8px",
            }}
          >
            <CircularProgress sx={{ color: "#7367F0" }} />
          </StackKit>
        )}

        <table className="w-full table-auto border-separate border-spacing-x-3 border-spacing-y-3">
          <thead>
            {paginatedData.length > 0 && (
              <tr className="text-[#fff]">
                <th className="text-center px-2 py-1 text-sm sm:text-base">
                  ردیف
                </th>
                {dataHeader?.map((header) => (
                  <th
                    key={header.id}
                    className="text-center px-2 py-1 text-sm sm:text-base"
                  >
                    {header.title}
                  </th>
                ))}
              </tr>
            )}
          </thead>

          {paginatedData.length > 0 && (
            <tbody>
              {paginatedData.map((row: any, rowIndex: number) => {
                const rowNumber =
                  (currentPage - 1) * itemsPerPage + rowIndex + 1;

                return (
                  <tr
                    key={row.id ?? rowIndex}
                    className="text-center text-[#fff]"
                    style={{ borderRadius: "8px", overflow: "hidden" }}
                  >
                    <td className="rounded-lg bg-gradient-to-r from-[#313131] to-[#252525] px-2 py-1">
                      {toPersianNumber(rowNumber)}
                    </td>

                    {dataHeader?.map((header) => (
                      <td
                        key={header.id}
                        className={`px-2 py-1 ${
                          header.id === "action"
                            ? "!bg-transparent"
                            : "rounded-lg bg-gradient-to-r bg-[#252525]"
                        }`}
                        style={{
                          maxWidth: isSmall ? "120px" : "150px",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          textAlign: "center",
                        }}
                      >
                        {row[header.id] ?? "-"}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          )}
        </table>
      </BoxKit>

      {dataTbody && dataTbody.length === 0 && !actionLoading && (
        <StackKit
          direction="row"
          alignItems="center"
          justifyContent="center"
          className="w-full p-12"
        >
          <TypoGraphyKit variant="h6">اطلاعاتی یافت نشد</TypoGraphyKit>
        </StackKit>
      )} 

      {totalPages > 1 && (
        <StackKit direction="row" justifyContent="center" sx={{ mt: 2 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, page) => handlePageChange(page)}
            sx={{
              "& .MuiPaginationItem-root": { color: "white" },
              "& .MuiPaginationItem-page": { backgroundColor: "#333" },
              "& .Mui-selected": {
                backgroundColor: "#7367F0",
                color: "white",
              },
            }}
            renderItem={(item) => {
              let page: string | undefined | number | null = item.page;

              if (typeof page === "number") {
                page = toPersianNumber(page);
              }

              if (item.type === "previous") {
                return <PaginationItem {...item} type="next" />;
              }

              if (item.type === "next") {
                return <PaginationItem {...item} type="previous" />;
              }

              return <PaginationItem {...item} page={page} />;
            }}
            size={isSmall ? "small" : "medium"}
          />
        </StackKit>
      )}
    </StackKit>
  );
};

export default TableWallet;